import { FlatInfo } from '@models/flat-info.model';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { IApiQueryOptions } from '@services/api/models/api-query-params.model';
import { FlatInfoService } from '@services/util/flat-info.service';
import { LogService } from '@services/util/log.service';
import { format, isToday, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HardCodedConfigService } from '../../../../../services/util/hard-coded-config.service';
import { ArrivalRow, DepartureRow } from '../models/arrival-departure.model';

@Injectable({
  providedIn: 'root'
})
export class ArrivalDepartureService {
  constructor(
    private bookingService: BookingClientService,
    private hardCodedConfig: HardCodedConfigService,
    private log: LogService,
    private flatInfoService: FlatInfoService
  ) {}

  /**
   * Get arrivals for today (check-in date is today)
   * @returns Observable of array of arrival rows
   */
  getArrivals(): Observable<ArrivalRow[]> {
    const today = new Date();
    const formattedToday = format(today, 'yyyy-MM-dd');

    // Get finalized bookings where start_date is today
    const queryOptions: IApiQueryOptions = {
      filters: {
        start_date: formattedToday,
        'bookingStatus.id': this.hardCodedConfig.BOOKING_STATUS_FINALIZED_ID
      }
    };

    return this.bookingService.getAllItems('', queryOptions).pipe(map((bookings) => this.transformBookingsToArrivals(bookings as IBookingModel[])));
  }

  /**
   * Get departures for today (check-out date is today)
   * @returns Observable of array of departure rows
   */
  getDepartures(): Observable<DepartureRow[]> {
    const today = new Date();
    const formattedToday = format(today, 'yyyy-MM-dd');

    // Get finalized bookings where end_date is today
    const queryOptions: IApiQueryOptions = {
      filters: {
        end_date: formattedToday,
        'bookingStatus.id': this.hardCodedConfig.BOOKING_STATUS_FINALIZED_ID
      }
    };

    return this.bookingService.getAllItems('', queryOptions).pipe(map((bookings) => this.transformBookingsToDepartures(bookings as IBookingModel[])));
  }

  /**
   * Transform bookings to arrival rows
   * @param bookings Bookings data from API
   * @returns Array of arrival rows
   */
  private transformBookingsToArrivals(bookings: IBookingModel[]): ArrivalRow[] {
    this.log.debug('Transforming bookings to arrivals', bookings);

    return bookings
      .map((booking) => {
        const flatInfo = this.flatInfoService.createFlatInfo(booking.flats?.[0]);
        return {
          id: booking.id!,
          flatInfo,
          checkInDate: parseISO(booking.start_date || ''),
          checkOutDate: parseISO(booking.end_date || ''),
          hasCheckedIn: booking.checked_in || false,
          checkInTime: booking.checkin_time
        };
      })
      .sort((a, b) => FlatInfo.compare(a.flatInfo, b.flatInfo));
  }

  /**
   * Transform bookings to departure rows
   * @param bookings Bookings data from API
   * @returns Array of departure rows
   */
  private transformBookingsToDepartures(bookings: IBookingModel[]): DepartureRow[] {
    this.log.debug('Transforming bookings to departures', bookings);
    // TODO: Filtering out bookings that are not finalized should be supported by the API
    return bookings
      .filter((booking) => booking.bookingStatus?.id === this.hardCodedConfig.BOOKING_STATUS_FINALIZED_ID)
      .map((booking) => {
        const flatInfo = this.flatInfoService.createFlatInfo(booking.flats?.[0]);

        return {
          id: booking.id!,
          flatInfo,
          checkInDate: parseISO(booking.start_date || ''),
          checkOutDate: parseISO(booking.end_date || ''),
          hasCheckedOut: booking.checked_out || false,
          checkOutTime: booking.checkout_time
        };
      })
      .sort((a, b) => FlatInfo.compare(a.flatInfo, b.flatInfo));
  }
}
